/*
 * ONBOARDING TIPS — the beacon/tip engine (docs/design/onboarding-tour.md §6).
 *
 * Delivery is PULL, not push: a milestone makes a small radiating BLOOM (a real
 * <button> wearing the garden's stage-7 flower crown, tab-flower.svg — the Garden
 * chip's own art) appear beside the control it talks about; the user's own click
 * opens the explanation popover. Dismissing it — Got it, Esc, or a press anywhere
 * else — stops the radiation but KEEPS the flower: a quiet bloom stays clickable
 * for re-reading, and wilts away RETIRE_DAYS distinct use-days after the LAST read
 * (re-reading renews the stay; its `when` predicate can end it sooner, the moment
 * the tip's context stops applying). Nothing auto-opens, nothing re-nags, nothing
 * blocks the app.
 *
 * The registry below is the whole content model: {id, anchor (selector|fn), titleKey/
 * bodyKey, when (predicate over app state), side, panel, during, priority}. Entries may
 * also carry sinceVersion — the future what's-new channel (announce a new feature to
 * users created before it shipped). v1 seeds none and the engine skips such entries
 * until that channel gets an arming rule, so the field is plumbing, not behavior.
 *
 * Evaluation runs at stable hooks (displayAll, tab switch, drill start/end, WL expand,
 * learn-band renders — one-line calls in main.js / learning_session.js), each pass
 * enforcing the §6 rules:
 *   - at most ONE RADIATING beacon visible; a side-panel one may coexist (2 absolute
 *     ceiling); priority picks the winner per region. Quiet (already-read) blooms ride
 *     along outside the ceiling — bounded by their predicates and the wilt clock;
 *   - at most 2 FIRST-READ popover opens per page visit — explore crumbs and re-reads
 *     of quiet blooms are free; after the second first-read closes, unread seed tips
 *     wait for the next visit;
 *   - no beacons while a learning-session or drill question screen is live, except
 *     entries explicitly marked during:"drill" (the space-pronounce tip IS the drill's);
 *   - none while an onboarding card or a Driver.js beat owns the stage;
 *   - read-days persist in the `onboarding` attr via ONBOARDING.tips (single-writer:
 *     this module never touches the attr itself) — the wilt clock ticks in distinct
 *     use-days (st.days), across devices.
 *
 * ARMING: only states done|skipped run beacons (fresh/offered still belong to the
 * welcome card, running to the guided beats). An ABSENT attr — every user predating the
 * tour — keeps the engine fully disarmed, same rule as onboarding.js; the what's-new
 * channel is the planned exception and arrives with its first seeded entry.
 *
 * EXPLORE MODE (welcome declined, state skipped): the guided run's teaching collapses
 * into a breadcrumb trail of chained beacons through the simplest full loop — Learn tab
 * → open a level list (Elementary first) → its Add pill → Start Learning → (first
 * graduation) the practice handoff hero — plus an escort crumb back to the Learn tab
 * when words already wait in the tray. Same engine, registry rows marked explore: true,
 * whose predicates hand over to each other as the state advances. Explore rows are
 * EXEMPT from the popover quota: each crumb is once-ever, but walking the trail end to
 * end takes more than two opens, and going dark two crumbs in defeats the trail.
 *
 * A11y (§11): the beacon is a keyboard-reachable button with an aria-label; the popover
 * is a dialog on PL.layers (Esc + outside-press) with focus moved in and restored out;
 * openings are announced via an aria-live region; prefers-reduced-motion pins the ring
 * animation to a static dot (CSS side, components/onboarding.css).
 *
 * ON ICE (owner call 2026-07-27): the blooms read as smoke puffs rather than
 * concentric rings, and vanish against small controls — the glow guide
 * (onboarding_guide.js) took over the explore path. BEACONS_ENABLED below keeps
 * every beacon dark by default while the whole engine stays runnable: ?beacons=on
 * re-arms it for one visit (the tips smoke harness runs on that), and flipping the
 * default back is this one expression. evaluate() still runs on every hook — it is
 * the guide's ride — and sessionFinished still counts drills/promotions (the guide
 * and the seed-tip gates read those regardless of who paints).
 */
var OB_TIPS;

(function () {
	"use strict";

	// The bloom kill switch — see ON ICE above. Not a config: a deliberate parking.
	var BEACONS_ENABLED = /[?&]beacons=on\b/.test(window.location.search);

	var MAX_OPENS = 2;      // FIRST-READ seed-tip opens per page visit (§6); explore rows + re-reads exempt
	var RETIRE_DAYS = 3;    // a quiet bloom wilts this many distinct use-days after its LAST read
	var opens = 0;
	var live = {};          // id → {btn, el, entry} — beacons currently in the DOM
	var popover = null;     // the ONE open popover, or null
	var liveRegion = null;

	function T() { return window.ONBOARDING && ONBOARDING.tips; }

	function el(tag, cls, text) {
		var node = document.createElement(tag);
		if (cls) { node.className = cls; }
		if (text != null) { node.textContent = text; }
		return node;
	}

	function announce(text) {
		if (!liveRegion) {
			liveRegion = el("div", "obLiveRegionClass");
			liveRegion.setAttribute("aria-live", "polite");
			document.body.appendChild(liveRegion);
		}
		liveRegion.textContent = text;
	}

	function visible(node) {
		return !!(node && node.isConnected && node.getClientRects().length);
	}

	function activeTab() {
		var mode = document.getElementById("centralModeId");
		return mode ? mode.getAttribute("data-tab") : null;
	}

	function langToSet() {
		var f = document.getElementById("langToValId");
		return !!(f && f.getAttribute("data-lang-code"));
	}

	function trayCount() {
		return (window.LRN && LRN.getTray) ? (LRN.getTray() || []).length : 0;
	}

	// The one expanded section, when it is a dictionary level list (the panel is an
	// accordion — at most one section is ever expanded).
	function expandedLevelSection() {
		return document.querySelector(".wlSectionClass.wlExpanded[data-wl-kind^='LEVEL_']");
	}

	// Where the explore trail points first for words: Elementary (LEVEL_A), or the first
	// level list this language pair actually renders.
	function firstLevelHeader() {
		return document.querySelector(".wlSectionClass[data-wl-kind='LEVEL_A'] .wlHeaderClass")
			|| document.querySelector(".wlSectionClass[data-wl-kind^='LEVEL_'] .wlHeaderClass");
	}

	// The learn band's hero doubles as the practice handoff ("Go to the practice now!")
	// once every tray word graduated — the two explore entries split on its label.
	function learnHeroIsHandoff() {
		var label = document.querySelector("#learnControlBandId .lrnStartLabelClass");
		return !!label && label.textContent === getRes("learnRecap_drillNow");
	}

	// A drill QUESTION screen is live (the finish screen hides the answer field again).
	function drillLive() {
		return visible(document.getElementById("answerId"));
	}

	// Any learning-session screen (ladder or recap) — beacons stay out of a lesson.
	function sessionScreenLive() {
		return drillLive() || visible(document.querySelector(".lrnBodyClass"));
	}

	// ── the registry ──────────────────────────────────────────────────────────────

	var REGISTRY = [
		// Explore mode: the declined-welcome path, one quiet pulse on the next thing to
		// press — a breadcrumb trail through the simplest full loop (find words → add
		// them → learn them → take them to practice). Chained by state, not by seen-ids —
		// a step the user found on their own (words already staged) simply never shows,
		// and the next one takes over.
		{
			id: "explore-learn-tab", anchor: "#tabLearnId", explore: true,
			titleKey: "obTipExpLearn_title", bodyKey: "obTipExpLearn_body",
			side: "tr", panel: "central", priority: 100,
			when: function (t) {
				return t.state() === "skipped" && trayCount() === 0 && activeTab() !== "learn";
			}
		},
		{
			// On the Learn tab with an empty tray the trail crosses into the side panel:
			// words come from the level lists, Elementary first.
			id: "explore-open-list", anchor: firstLevelHeader, explore: true,
			titleKey: "obTipExpList_title", bodyKey: "obTipExpList_body",
			side: "tr", panel: "side", priority: 98,
			when: function (t) {
				return t.state() === "skipped" && trayCount() === 0
					&& activeTab() === "learn" && !expandedLevelSection();
			}
		},
		{
			// The opened list's Add pill — the control that actually fills the tray.
			id: "explore-add-words", explore: true,
			anchor: function () {
				var sec = expandedLevelSection();
				return sec ? sec.querySelector(".wlAddPracticeClass") : null;
			},
			titleKey: "obTipExpAdd_title", bodyKey: "obTipExpAdd_body",
			side: "tr", panel: "side", priority: 97,
			when: function (t) {
				return t.state() === "skipped" && trayCount() === 0 && activeTab() === "learn";
			}
		},
		{
			// Words wait in the tray but the user wandered off — escort them back. Its own
			// id, not a reuse of explore-learn-tab: that crumb may already be spent from
			// the empty-tray stage, and this moment earns its own once-ever.
			id: "explore-goto-tray", anchor: "#tabLearnId", explore: true,
			titleKey: "obTipExpTray_title", bodyKey: "obTipExpTray_body",
			side: "tr", panel: "central", priority: 96,
			when: function (t) {
				return t.state() === "skipped" && trayCount() > 0 && activeTab() !== "learn";
			}
		},
		{
			id: "explore-learn-start", explore: true,
			anchor: function () {
				return document.querySelector("#learnControlBandId .lrnStartButtonClass");
			},
			titleKey: "obTipExpStart_title", bodyKey: "obTipExpStart_body",
			side: "tr", panel: "central", priority: 95,
			when: function (t) {
				return t.state() === "skipped" && trayCount() > 0 && !learnHeroIsHandoff();
			}
		},
		{
			id: "explore-take-practice", explore: true,
			anchor: function () {
				return document.querySelector("#learnControlBandId .lrnStartButtonClass");
			},
			titleKey: "obTipExpDrill_title", bodyKey: "obTipExpDrill_body",
			side: "tr", panel: "central", priority: 90,
			when: function (t) { return t.state() === "skipped" && learnHeroIsHandoff(); }
		},

		// The seed tips (§6 table). save-progress is deliberately NOT here — its whole
		// deliverable is the restyled persistent register link (login_main.js), no beacon.
		{
			// The drill's own tip, the one during:"drill" entry — on the progress band
			// (the counter pill), never on the word card itself.
			id: "space-pronounce", anchor: "#counterBoxId",
			titleKey: "obTipSpacePron_title", bodyKey: "obTipSpacePron_body",
			side: "r", panel: "central", during: "drill", priority: 80,
			when: function (t) { return t.drills() < 2; }
		},
		{
			// First time the "💧 N thirsty" chip appears (anchor visibility IS the trigger).
			id: "thirsty-watering", anchor: "#thirstyChipId",
			titleKey: "obTipThirsty_title", bodyKey: "obTipThirsty_body",
			side: "tr", panel: "central", priority: 70,
			when: function () { return true; }
		},
		{
			// After the 2nd finished drill — retires the reverse-mode discoverability debt.
			id: "direction-cluster", anchor: "#practiceDirBoxId",
			titleKey: "obTipDirection_title", bodyKey: "obTipDirection_body",
			side: "tr", panel: "central", priority: 60,
			when: function (t) { return t.drills() >= 2; }
		},
		{
			// First promotion (a plant grew a stage) — the garden got its first news.
			id: "my-garden",
			anchor: function () {
				return document.querySelector(".wlSectionClass[data-wl-kind='GARDEN'] .wlHeaderClass");
			},
			titleKey: "obTipMyGarden_title", bodyKey: "obTipMyGarden_body",
			side: "tr", panel: "side", priority: 50,
			when: function (t) { return t.promoted(); }
		},
		{
			// An expanded level list's Add pill — gated off day 1 (the Anki lesson: don't
			// invite bulk intake before the starter batch settled).
			id: "add-pill",
			anchor: function () {
				return document.querySelector(
					".wlSectionClass.wlExpanded[data-wl-kind^='LEVEL'] .wlAddPracticeClass");
			},
			titleKey: "obTipAddPill_title", bodyKey: "obTipAddPill_body",
			side: "tr", panel: "side", priority: 40,
			when: function (t) { return t.days() >= 2; }
		},
		{
			// First Dictionary visit with a learning language set.
			id: "dict-lookup", anchor: "#lookupBoxId",
			titleKey: "obTipDictLookup_title", bodyKey: "obTipDictLookup_body",
			side: "tr", panel: "central", priority: 30,
			when: function () { return activeTab() === "dict" && langToSet(); }
		},
		{
			// The settings gear, once practicing is a habit (3rd distinct day of use).
			id: "settings-gear", anchor: "#prefButtonId",
			titleKey: "obTipGear_title", bodyKey: "obTipGear_body",
			side: "tr", panel: "central", priority: 20,
			when: function (t) { return t.days() >= 3; }
		}
	];

	// ── beacons ───────────────────────────────────────────────────────────────────

	function resolveAnchor(entry) {
		return typeof entry.anchor === "function"
			? entry.anchor() : document.querySelector(entry.anchor);
	}

	function removeBeacon(id) {
		var b = live[id];
		if (!b) { return; }
		delete live[id];
		if (b.btn && b.btn.parentNode) { b.btn.parentNode.removeChild(b.btn); }
		if (b.el && b.el.isConnected && !b.el.querySelector(".obBeaconClass")) {
			b.el.classList.remove("obBeaconHostClass");
		}
	}

	function removeAll() {
		for (var id in live) {
			if (live.hasOwnProperty(id)) { removeBeacon(id); }
		}
	}

	// ── the bloom's halo art ──────────────────────────────────────────────────────
	// The radiating wave repeats the flower's own silhouette: a six-scallop contour
	// (petal tips at 60° steps, notches between) drawn as arcs, stroked in the bloom's
	// rose red and scaled outward by CSS. Sparks reuse tab-flower.svg's four-point
	// glint path, each parked in a group rotated to a notch angle so one arm lies on
	// the radius — crossing every ring it meets square-on while it rides outward.
	var SVG_NS = "http://www.w3.org/2000/svg";
	var RING_D = "M-3.6 -6.24 A3.7 3.7 0 0 1 3.6 -6.24 A3.7 3.7 0 0 1 7.2 0"
		+ " A3.7 3.7 0 0 1 3.6 6.24 A3.7 3.7 0 0 1 -3.6 6.24"
		+ " A3.7 3.7 0 0 1 -7.2 0 A3.7 3.7 0 0 1 -3.6 -6.24 Z";
	var SPARK_D = "M0 -3.9 Q0.6 -0.6 4 0 Q0.6 0.6 0 3.9 Q-0.6 0.6 -4 0 Q-0.6 -0.6 0 -3.9 Z";
	var SPARK_ANGLES = [30, 150, 210, 330];   // notch angles — never sitting on a petal tip

	function svgEl(tag, attrs) {
		var node = document.createElementNS(SVG_NS, tag);
		for (var k in attrs) {
			if (attrs.hasOwnProperty(k)) { node.setAttribute(k, attrs[k]); }
		}
		return node;
	}

	var WAVE_DELAYS = [0, 0.9, 1.8];        // three staggered waves per 2.7s cycle
	var SPARK_DELAYS = [0, 0.65, 1.35, 2.05];

	function haloSvg() {
		var svg = svgEl("svg", { viewBox: "-24 -24 48 48", "aria-hidden": "true" });
		for (var r = 0; r < WAVE_DELAYS.length; r++) {
			// Each wave is a group of two strokes on the same contour: a pane-colored
			// under-stroke and the rose line over it — the old dot's keyline trick, so
			// the wave stays legible on saturated surfaces (the green Start hero).
			var wave = svgEl("g", { "class": "obWaveClass" });
			wave.appendChild(svgEl("path", { d: RING_D, "class": "obRingUnderClass" }));
			wave.appendChild(svgEl("path", { d: RING_D, "class": "obRingClass" }));
			wave.style.animationDelay = WAVE_DELAYS[r] + "s";
			svg.appendChild(wave);
		}
		for (var s = 0; s < SPARK_ANGLES.length; s++) {
			var g = svgEl("g", { transform: "rotate(" + SPARK_ANGLES[s] + ")" });
			var spark = svgEl("path", { d: SPARK_D, "class": "obSparkClass" });
			spark.style.animationDelay = SPARK_DELAYS[s] + "s";
			g.appendChild(spark);
			svg.appendChild(g);
		}
		return svg;
	}

	function addBeacon(entry, anchorEl, quiet) {
		var btn = el("button", "obBeaconClass" + (quiet ? " obBeaconQuietClass" : ""));
		btn.type = "button";
		btn.setAttribute("data-ob-side", entry.side || "tr");
		btn.setAttribute("aria-label", getRes(entry.titleKey));
		if (!quiet) {
			var halo = el("span", "obBloomHaloClass");
			halo.setAttribute("aria-hidden", "true");
			halo.appendChild(haloSvg());
			btn.appendChild(halo);
		}
		var bloom = document.createElement("img");
		bloom.className = "obBloomClass";
		bloom.alt = "";
		bloom.src = "/image/garden/tab-flower.svg";
		btn.appendChild(bloom);
		btn.addEventListener("click", function (e) {
			e.stopPropagation();   // the anchor underneath must not also act on this press
			openPopover(entry, btn);
		});
		anchorEl.classList.add("obBeaconHostClass");
		anchorEl.appendChild(btn);
		live[entry.id] = { btn: btn, el: anchorEl, entry: entry, quiet: !!quiet };
	}

	// ── the popover ───────────────────────────────────────────────────────────────

	function positionPopover(pop, btn) {
		var r = btn.isConnected ? btn.getBoundingClientRect() : null;
		if (!r) { return; }
		var w = pop.offsetWidth, h = pop.offsetHeight;
		var left = Math.min(Math.max(8, r.left - 12), window.innerWidth - w - 8);
		var top = r.bottom + 10;
		if (top + h > window.innerHeight - 8) { top = Math.max(8, r.top - h - 10); }
		pop.style.left = left + "px";
		pop.style.top = top + "px";
	}

	function openPopover(entry, btn) {
		var t = T();
		if (popover || !t) { return; }
		// The quota counts only FIRST reads of seed tips: explore crumbs are exempt, and
		// re-opening a quiet bloom is the user asking — never chargeable.
		if (!entry.explore && !t.seenDay(entry.id)) { opens++; }
		var title = getRes(entry.titleKey);
		var body = getRes(entry.bodyKey);

		var pop = el("div", "obTipPopClass");
		pop.setAttribute("role", "dialog");
		pop.setAttribute("aria-label", title);
		pop.appendChild(el("div", "obTipTitleClass", title));
		pop.appendChild(el("div", "obTipBodyClass", body));
		var foot = el("div", "obTipFootClass");
		var ok = el("button", "pl-btn-pill obPrimaryBtnClass obTipOkClass", getRes("obGotIt_button"));
		ok.type = "button";
		foot.appendChild(ok);
		pop.appendChild(foot);

		var focusToken = PL.focus.remember();
		document.body.appendChild(pop);
		positionPopover(pop, btn);
		var reposition = function () { positionPopover(pop, btn); };
		window.addEventListener("resize", reposition);
		window.addEventListener("scroll", reposition, true);

		var layer = PL.layers.push({
			el: pop,
			anchors: [btn],
			onClose: function () {
				window.removeEventListener("resize", reposition);
				window.removeEventListener("scroll", reposition, true);
				pop.remove();
				popover = null;
				t.markSeen(entry.id);   // ANY dismissal re-stamps the read day (the wilt clock)
				if (focusToken) { focusToken.restore({ ifWithin: pop }); }
				removeBeacon(entry.id);
				// Re-sync now: the just-read bloom comes straight back QUIET (radiation off,
				// still clickable), and seed beacons past the quota get pruned.
				evaluate();
			}
		});
		popover = { layer: layer, entry: entry };
		ok.addEventListener("click", function () { layer.close("api"); });
		setTimeout(function () { ok.focus(); }, 0);
		announce(title + ". " + body);
	}

	// ── evaluation ────────────────────────────────────────────────────────────────

	// One pass: pick the highest-priority eligible entry per region (central / side
	// panel), then make the DOM match. Idempotent and cheap — hooks may call it freely.
	function evaluate() {
		// The glow guide rides every hook that feeds this engine (that is the whole
		// point of keeping evaluate live while the blooms sleep) — sync it FIRST,
		// before any of the early returns below.
		if (window.OB_GUIDE) { OB_GUIDE.sync(); }
		if (!BEACONS_ENABLED) { removeAll(); return; }
		var t = T();
		if (!t || !t.armed() || (t.state() !== "done" && t.state() !== "skipped")) {
			removeAll();
			return;
		}
		if (popover) { return; }             // an open popover freezes the set
		// An onboarding card owns the stage — one message at a time.
		if (document.querySelector(".obCardClass")) {
			removeAll();
			return;
		}

		var inDrill = drillLive();
		var inSession = sessionScreenLive();
		var want = { central: null, side: null };   // the RADIATING beacon per region
		var quiet = {};                             // id → {entry, el}: read-again blooms

		for (var i = 0; i < REGISTRY.length; i++) {
			var entry = REGISTRY[i];
			if (entry.sinceVersion) { continue; }              // what's-new channel: not armed in v1
			if (entry.during === "drill" ? !inDrill : inSession) { continue; }
			var sd = t.seenDay(entry.id);
			if (sd && t.days() - sd >= RETIRE_DAYS) { continue; }   // wilted for good
			var anchorEl = resolveAnchor(entry);
			if (!visible(anchorEl)) { continue; }
			if (entry.when && !entry.when(t)) { continue; }
			if (sd) {
				// Already read: rides along as a quiet bloom wherever it still applies —
				// outside the region ceiling, bounded by predicate + wilt clock.
				quiet[entry.id] = { entry: entry, el: anchorEl };
				continue;
			}
			var region = entry.panel === "side" ? "side" : "central";
			if (want[region]) { continue; }                    // registry is priority-ordered
			// Seed quota spent — unread seed tips wait for the next visit; the explore
			// trail keeps flowing (its crumbs are the declined tour's whole teaching).
			if (!entry.explore && opens >= MAX_OPENS) { continue; }
			want[region] = { entry: entry, el: anchorEl };
		}

		// Sync: drop what shouldn't be there (or whose anchor was re-rendered, or whose
		// MODE flipped active↔quiet), add the rest. btn.isConnected matters as much as
		// the anchor: a host that repaints its CONTENT with .html() (the WL Add pill
		// does, on every reveal) kills the beacon button while the host element itself
		// survives — without this check that id becomes a phantom "already shown" entry
		// and the beacon never returns.
		for (var id in live) {
			if (!live.hasOwnProperty(id)) { continue; }
			var keep = (want.central && want.central.entry.id === id
					&& want.central.el === live[id].el && !live[id].quiet)
				|| (want.side && want.side.entry.id === id
					&& want.side.el === live[id].el && !live[id].quiet)
				|| (quiet[id] && quiet[id].el === live[id].el && live[id].quiet);
			if (!keep || !live[id].el.isConnected || !live[id].btn.isConnected) { removeBeacon(id); }
		}
		if (want.central && !live[want.central.entry.id]) { addBeacon(want.central.entry, want.central.el, false); }
		if (want.side && !live[want.side.entry.id]) { addBeacon(want.side.entry, want.side.el, false); }
		for (var qid in quiet) {
			if (quiet.hasOwnProperty(qid) && !live[qid]) { addBeacon(quiet[qid].entry, quiet[qid].el, true); }
		}
	}

	// Drill finished (main.js session-end hook): count it, remember a first promotion.
	// Deliberately NO evaluate here — the finish screen belongs to the guided beat and
	// the signup nudge when they apply; the next natural hook re-evaluates.
	function sessionFinished(grew, wordOutcomes) {
		var t = T();
		if (t) { t.drillFinished(!!(grew && grew.length), wordOutcomes ? wordOutcomes.length : null); }
	}

	OB_TIPS = { evaluate: evaluate, sessionFinished: sessionFinished };

	// Boot pass — after the app's own ready handlers painted the first view, and after
	// onboarding.js's 350ms welcome defer, so a welcome card wins the stage check above.
	$(function () {
		// Warm the bloom art so the very first beacon paints flower-first, not ring-first
		// (a cold cache showed the wave alone for a frame or two).
		var warm = new Image();
		warm.src = "/image/garden/tab-flower.svg";
		setTimeout(evaluate, 700);
	});

})();
